/**
 * TimezoneSelect — small native-select dropdown for picking the viewing
 * timezone on the public booking page.
 *
 * Native `<select>` is used deliberately: ~400 IANA timezones with built-in
 * type-to-search beats a custom searchable popover for this volume, and
 * mobile pickers get the OS-native wheel for free. The visual shell is
 * styled to match the surrounding metadata row so it doesn't pop out — it
 * reads as a button with the globe icon, not a generic form input.
 */

import { ChevronDown } from '@untitledui/icons';

import { cx } from '@/utils/cx';

interface TimezoneSelectProps {
  value: string;
  onChange: (next: string) => void;
  options: readonly string[];
  className?: string;
}

export function TimezoneSelect({
  value,
  onChange,
  options,
  className,
}: TimezoneSelectProps) {
  return (
    <label
      className={cx(
        // `focus-within` mirrors the focus ring up to the visible label so
        // keyboard users see WHERE focus landed even though the actual
        // `<select>` is transparent and absolutely positioned. Without this
        // the focus indicator was effectively invisible: the OS draws the
        // ring on the transparent select, not on the visible chip we
        // render. `rounded-md` matches the chip's `px-1` shape; the offset
        // pulls the ring away from the surrounding text. Use the design
        // system focus token (`--color-focus-ring`) so it adapts to dark
        // mode and stays consistent with Button / Input focus styling.
        'group relative inline-flex cursor-pointer items-center gap-1 rounded-md px-1 -mx-1 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] focus-within:outline-none focus-within:ring-2 focus-within:ring-[var(--color-focus-ring)] focus-within:ring-offset-1 focus-within:ring-offset-[var(--color-bg-primary)] focus-within:text-[var(--color-text-primary)]',
        className
      )}
    >
      <span className='underline decoration-dotted underline-offset-2'>
        {value.replace(/_/g, ' ')}
      </span>
      <ChevronDown
        className='size-3 text-[var(--color-text-tertiary)] transition-transform group-hover:text-[var(--color-text-secondary)] group-focus-within:text-[var(--color-text-secondary)]'
        aria-hidden='true'
      />
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        // The native select sits on top of the visible label, fully
        // transparent and full-bleed, so the OS picker is triggered by a
        // click anywhere on the chip — gives us native search-by-typing and
        // the mobile system picker for free. The transparent overlay also
        // means the browser's default focus ring is invisible, so the
        // wrapper label's `focus-within:ring-…` provides the visible
        // keyboard-focus affordance instead.
        className='absolute inset-0 h-full w-full cursor-pointer opacity-0 focus:outline-none'
        aria-label='Change timezone'
      >
        {options.map(tz => (
          <option key={tz} value={tz}>
            {tz.replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  );
}
